// Session state. GET /auth/me decides signed-in or not on load. A signed-out
// visitor is a guest: "/" is the guest chat and needs no session. Any 401
// from a call made with a session drops the user and returns the app to the
// sign-in page with ?next= so the officer lands back where they were: the
// admin console signs in at /portal, everything else at /masuk.
//
// A signed-in non-admin who still owes the current User Agreement
// (agreementRequired from /auth/me, or any 403 { code: "agreement_required" })
// is marked here; the router then shows only the agreement step and returns
// to the page they were on once it is accepted.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import * as API from "./api";
import { navigate } from "./router";
import { isAdminPath, LOGIN_PATH, PORTAL_PATH } from "./shell/nav";

export type AuthState =
  | { status: "loading"; user: null }
  | { status: "anon"; user: null; expired?: boolean }
  | { status: "authed"; user: API.User; agreementRequired?: boolean };

interface AuthValue {
  state: AuthState;
  signIn: (user: API.User) => void;
  signOut: () => Promise<void>;
  setUser: (user: API.User) => void;
  /** Reads GET /auth/me again (after a role switch); resolves with the user, or null when signed out. */
  refresh: () => Promise<API.User | null>;
  /** The current User Agreement is accepted: the agreement step is done. */
  agreementAccepted: () => void;
}

const AuthContext = createContext<AuthValue | null>(null);

/** The sign-in page for `path`: /portal for the admin console, /masuk otherwise. */
export function signInPathFor(path: string): string {
  return isAdminPath(path) ? PORTAL_PATH : LOGIN_PATH;
}

/** The sign-in href for the current location, with ?next= back to it. */
export function loginHref(): string {
  if (typeof location === "undefined") return LOGIN_PATH;
  const here = location.pathname + location.search;
  if (location.pathname === LOGIN_PATH || location.pathname === PORTAL_PATH) return here;
  const target = signInPathFor(location.pathname);
  return here && here !== "/" ? `${target}?next=${encodeURIComponent(here)}` : target;
}

/** True when this signed-in state must show the agreement step only (never admin or pending). */
export function owesAgreement(state: AuthState): boolean {
  return state.status === "authed" && state.agreementRequired === true && state.user.role !== "admin" && state.user.role !== "pending";
}

function sessionState(session: API.Session): AuthState {
  return { status: "authed", user: session.user, agreementRequired: session.agreementRequired };
}

export function AuthProvider({ children, initial }: { children: ReactNode; initial?: AuthState }) {
  const [state, setState] = useState<AuthState>(initial ?? { status: "loading", user: null });
  const current = useRef(state);
  current.current = state;

  useEffect(() => {
    if (initial) return;
    let alive = true;
    API.me().then(
      (session) => alive && setState(sessionState(session)),
      () => alive && setState({ status: "anon", user: null }),
    );
    return () => {
      alive = false;
    };
  }, [initial]);

  useEffect(() => {
    API.setUnauthorizedHandler(() => {
      // A guest has no session to lose: a 401 then is only that call failing.
      if (current.current.status !== "authed") return;
      setState({ status: "anon", user: null, expired: true });
      navigate(loginHref(), { replace: true });
    });
    API.setAgreementRequiredHandler(() => {
      setState((s) => (s.status === "authed" && s.user.role !== "admin" && s.user.role !== "pending" && !s.agreementRequired ? { ...s, agreementRequired: true } : s));
    });
    API.setAccountDisabledHandler(() => {
      setState((s) => (s.status === "authed" && s.user.role !== "admin" && s.user.status !== "nonaktif" ? { ...s, user: { ...s.user, status: "nonaktif" } } : s));
    });
    return () => {
      API.setUnauthorizedHandler(null);
      API.setAgreementRequiredHandler(null);
      API.setAccountDisabledHandler(null);
    };
  }, []);

  const signIn = useCallback((user: API.User) => setState({ status: "authed", user }), []);
  const setUser = useCallback(
    (user: API.User) => setState((s) => ({ status: "authed", user, agreementRequired: s.status === "authed" ? s.agreementRequired : undefined })),
    [],
  );
  const refresh = useCallback(async () => {
    try {
      const session = await API.me();
      setState(sessionState(session));
      return session.user;
    } catch (err) {
      if (err instanceof API.ApiError && err.status === 401 && current.current.status === "authed") {
        setState({ status: "anon", user: null, expired: true });
        navigate(loginHref(), { replace: true });
        return null;
      }
      // Offline or a server error: the session stays as it was.
      return current.current.status === "authed" ? current.current.user : null;
    }
  }, []);
  const agreementAccepted = useCallback(() => setState((s) => (s.status === "authed" ? { ...s, agreementRequired: false } : s)), []);
  const signOut = useCallback(async () => {
    const admin = current.current.status === "authed" && current.current.user.role === "admin";
    try {
      await API.logout();
    } finally {
      setState({ status: "anon", user: null });
      // A user returns to the guest chat; the admin to its own sign-in.
      navigate(admin ? PORTAL_PATH : "/", { replace: true });
    }
  }, []);

  const value = useMemo(() => ({ state, signIn, signOut, setUser, refresh, agreementAccepted }), [state, signIn, signOut, setUser, refresh, agreementAccepted]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthValue {
  const v = useContext(AuthContext);
  if (!v) throw new Error("useAuth outside AuthProvider");
  return v;
}

/** The signed-in user; only call inside screens rendered for an authed session. */
export function useUser(): API.User {
  const { state } = useAuth();
  if (state.status !== "authed") throw new Error("useUser without a session");
  return state.user;
}

/** The signed-in user, or null for a guest. */
export function useMaybeUser(): API.User | null {
  const { state } = useAuth();
  return state.status === "authed" ? state.user : null;
}
